// @flow strict

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import {useDispatch, useSelector} from 'react-redux';

import {selectApplicantInformation, selectUserInfo} from 'src/selectors/crm';

import {useInflight} from 'src/hooks/redux';

import {
  getApplicantInformation,
  updateCrmApplicantInformationData,
} from 'src/action-creators/crm';

import {
  Card,
  CardHeader,
  CardTitle,
  CardActions,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {
  Table,
  BasicSingleCell,
} from '@spaced-out/ui-design-system/lib/components/Table';
import {TruncatedTextWithTooltip} from '@spaced-out/ui-design-system/lib/components/TruncatedTextWithTooltip';
import {hasCrmPermission, CRM_PERMISSIONS} from 'src/utils/crm-new/common';

import {
  SubTitleSmall,
  ButtonTextSmall,
  BodyMedium,
  SubTitleLarge,
  BodyLarge,
  SubTitleMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';

//$FlowFixMe[untyped-import]
import {formatMomentDateWithFormat} from 'src/utils/date-time';

import AttachFile from './attach-file.jsx';

import css from './style.css';

//Move to util later
const getApiPrefix = (role: string) => {
  return role === 'AGENCY' ? 'trmapi/agency' : '/trmapi';
};

const ApplicantAttachments = ({
  jobId,
  applicantId,
  hideAttachmentsApplicationSpecificAttachments = false,
  showActionButtons = true,
}: {
  jobId: number,
  applicantId: number,
  hideAttachmentsApplicationSpecificAttachments?: boolean,
  showActionButtons?: boolean,
}): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const dispatch = useDispatch();
  const [showAttachFileModal, setShowAttachFileModal] = React.useState(false);
  const [attachmentType, setAttachmentType] = React.useState('CANDIDATE');
  const [editFileName, setEditFileName] = React.useState('');
  const [editAttachmentType, setEditAttachmentType] = React.useState('');
  const [editFileId, setEditFileId] = React.useState(0);
  const [isEdit, setIsEdit] = React.useState(false);
  const applicantDetails = useSelector(selectApplicantInformation);

  const candidateAttachments = [
    {
      label: t('Contact_details', 'Contact details'),
      value: 'CONTACT',
    },
    {label: t('Resume', 'Resume'), value: 'RESUME'},
    {
      label: t('Work_experience', 'Work experience'),
      value: 'WORK_EXP_DOCUMENTS',
    },
    {
      label: t('Address_proof', 'Address proof'),
      value: 'ADDRESS_PROOF',
    },
    {
      label: t('Photo_ID_proof', 'Photo ID proof'),
      value: 'PHOTO_ID_PROOF',
    },
    {
      label: t('Educational_documents', 'Educational documents'),
      value: 'EDUCATION_DOCUMENTS',
    },
    {label: t('Others', 'Others'), value: 'OTHER'},
    {label: t('Candidate_photo', 'Candidate photo'), value: 'CANDIDATE_PHOTO'},
  ];

  const applicationAttachments = [
    {label: t('Resume', 'Resume'), value: 'RESUME'},
    {label: t('Review', 'Review'), value: 'REVIEW'},
    {label: t('Offer', 'Offer'), value: 'OFFER'},
    {label: t('Others', 'Others'), value: 'OTHER'},
  ];

  const isApplicantsLoading = useInflight(
    getApplicantInformation(applicantId, jobId.toString()),
  );

  const {organization: organizationInfo, user: userInfo} =
    useSelector(selectUserInfo) ?? {};

  React.useEffect(() => {
    if (!applicantDetails && Boolean(applicantId) && Boolean(jobId)) {
      dispatch(getApplicantInformation(applicantId, jobId.toString()));
    }
  }, []);

  if (isApplicantsLoading) {
    return <CircularLoader />;
  }

  if (!applicantDetails) {
    return null;
  }

  const currentApplication = applicantDetails.applications.find(
    (app) => app.jobId === jobId,
  );

  const attachments = applicantDetails.candidateAttachments;

  const {integrationType = 'NONE'} = organizationInfo;

  const apiPrefix = getApiPrefix(userInfo?.roleTitle ?? '');

  const attachmentTableHeader = [
    {
      key: 'fileName',
      label: t('File_Name', 'File Name'),
      render: ({data}) => {
        return (
          <BasicSingleCell className={css.nameColumn}>
            <div className={css.flex}>
              <SubTitleSmall>
                <Link
                  href={`${apiPrefix}/api/attachments/${data.id}/download`}
                  target="_blank"
                  underline={false}
                >
                  <TruncatedTextWithTooltip>
                    {data.filename ?? '-'}
                  </TruncatedTextWithTooltip>
                </Link>
              </SubTitleSmall>
              {integrationType === 'TALEO' ? (
                <Tooltip
                  title={
                    data.taleoSynced
                      ? t('Synced_with_Taleo', 'Synced with Taleo')
                      : t('Not_Synced_with_Taleo', 'Not Synced with Taleo')
                  }
                  elevation="modal"
                >
                  <Icon
                    color={data.taleoSynced ? 'success' : 'danger'}
                    name={
                      data.taleoSynced ? 'check-circle' : 'exclamation-circle'
                    }
                  />
                </Tooltip>
              ) : null}
            </div>
          </BasicSingleCell>
        );
      },
    },
    {
      key: 'attachmentType',
      label: t('Type', 'Type'),
      render: ({data}) => {
        const attachment = [
          ...candidateAttachments,
          ...applicationAttachments,
        ].find((attach) => attach.value === data.attachmentType);
        return (
          <BasicSingleCell className={css.attachmentTypeColumn}>
            <BodyMedium>
              <TruncatedTextWithTooltip>
                {attachment ? attachment.label : '-'}
              </TruncatedTextWithTooltip>
            </BodyMedium>
          </BasicSingleCell>
        );
      },
      sortable: false,
    },
    {
      key: 'updatedBy',
      label: t('Updated_by', 'Updated by'),
      render: ({data}) => {
        return (
          <BasicSingleCell className={css.updatedByColumn}>
            <BodyMedium>
              <TruncatedTextWithTooltip>
                {data.updatedByName || '-'}
              </TruncatedTextWithTooltip>
            </BodyMedium>
          </BasicSingleCell>
        );
      },
      sortable: false,
    },
    {
      key: 'updatedOn',
      label: t('Updated_on', 'Updated on'),
      render: ({data}) => {
        return (
          <BasicSingleCell className={css.updatedOnColumn}>
            <BodyMedium>
              <TruncatedTextWithTooltip>
                {formatMomentDateWithFormat(data.updatedOn)}
              </TruncatedTextWithTooltip>
            </BodyMedium>
          </BasicSingleCell>
        );
      },
      sortable: false,
    },
    {
      key: 'actions',
      label: '',
      render: ({data}) => {
        return (
          <BasicSingleCell className={css.flex}>
            {hasCrmPermission(CRM_PERMISSIONS.CRUD_ATTACHMENT) &&
              showActionButtons && (
                <Button
                  iconLeftName="pen"
                  type="ghost"
                  onClick={() => {
                    setShowAttachFileModal(true);
                    setAttachmentType('CANDIDATE');
                    setEditFileName(data.filename);
                    setEditAttachmentType(data.attachmentType);
                    setEditFileId(data.id);
                    setIsEdit(true);
                  }}
                  size="small"
                />
              )}
          </BasicSingleCell>
        );
      },
      sortable: false,
    },
  ];

  if (!applicantDetails) {
    return null;
  }
  return (
    <div className={css.attachmentsContainer}>
      {currentApplication && !hideAttachmentsApplicationSpecificAttachments ? (
        <Card
          classNames={{
            wrapper: css.card,
          }}
        >
          <CardHeader>
            <CardTitle>
              {t(
                'Application_Specific_Attachments',
                'Application Specific Attachments',
              )}
            </CardTitle>
            {hasCrmPermission(CRM_PERMISSIONS.CRUD_ATTACHMENT) &&
              showActionButtons && (
                <CardActions>
                  <Button
                    children={t('Attach_a_file', 'Attach a file')}
                    size="small"
                    type="tertiary"
                    iconRightName="link"
                    onClick={() => {
                      setShowAttachFileModal(true);
                      setAttachmentType('APPLICATION');
                    }}
                  />
                </CardActions>
              )}
          </CardHeader>
          <CardContent className={css.cardContent}>
            <Table
              borderRadius="12px"
              entries={
                attachments.filter(
                  (attachment) => attachment.jobId === jobId,
                ) || []
              }
              headers={attachmentTableHeader}
              stickyHeader
            />
          </CardContent>
        </Card>
      ) : null}

      <Card
        classNames={{
          wrapper: css.card,
        }}
      >
        <CardHeader>
          <CardTitle>
            {t(
              'Candidate_Specific_Attachments',
              'Candidate Specific Attachments',
            )}
          </CardTitle>
          {hasCrmPermission(CRM_PERMISSIONS.CRUD_ATTACHMENT) &&
            showActionButtons && (
              <CardActions>
                <Button
                  children={t('Attach_a_file', 'Attach a file')}
                  size="small"
                  type="tertiary"
                  iconRightName="link"
                  onClick={() => {
                    setShowAttachFileModal(true);
                    setAttachmentType('CANDIDATE');
                  }}
                />
              </CardActions>
            )}
        </CardHeader>
        <CardContent className={css.cardContent}>
          <Table
            borderRadius="12px"
            entries={
              attachments.filter((attachment) => attachment.jobId === 0) || []
            }
            headers={attachmentTableHeader}
            stickyHeader
          />
        </CardContent>
      </Card>
      {showAttachFileModal ? (
        <AttachFile
          isOpen={showAttachFileModal}
          candidateId={applicantDetails.id}
          jobId={jobId}
          onClose={() => {
            setShowAttachFileModal(false);
            if (isEdit) {
              setEditFileName('');
              setEditAttachmentType('');
              setIsEdit(false);
            }
          }}
          title={t('Upload_Attachment', 'Upload Attachment')}
          attachmentType={attachmentType}
          initialFileName={editFileName}
          initialAttachmentType={editAttachmentType}
          onSubmit={(response) => {
            dispatch(
              updateCrmApplicantInformationData({
                ...applicantDetails,
                candidateAttachments: isEdit
                  ? applicantDetails.candidateAttachments.map((attachment) =>
                      attachment.id === editFileId ? response : attachment,
                    )
                  : [...applicantDetails.candidateAttachments, response],
              }),
            );
            setShowAttachFileModal(false);
            setEditFileName('');
            setEditAttachmentType('');
            setIsEdit(false);
          }}
          onDeleteFile={(deletedFileId) => {
            dispatch(
              updateCrmApplicantInformationData({
                ...applicantDetails,
                candidateAttachments:
                  applicantDetails.candidateAttachments.filter(
                    (attachment) => attachment.id !== deletedFileId,
                  ),
              }),
            );
            setShowAttachFileModal(false);
            setEditFileName('');
            setEditAttachmentType('');
            setIsEdit(false);
          }}
          isEdit={isEdit}
          editFileId={editFileId}
        />
      ) : null}
    </div>
  );
};

export default ApplicantAttachments;
